const BASE_URL = import.meta.env.VITE_ADMIN_API_URL ?? '/api'

interface ApiErrorBody {
  status: number
  message: string
  code?: string
}

const USER_FRIENDLY_MESSAGES: Record<string, string> = {
  EMAIL_NOT_AUTHORIZED: 'This email is not authorized for admin access.',
  INVALID_OTP: 'Invalid verification code. Please try again.',
  OTP_EXPIRED: 'Verification code has expired. Request a new one.',
  OTP_MAX_ATTEMPTS: 'Too many failed attempts. Request a new code.',
  RATE_LIMITED: 'Too many attempts. Please wait a moment and try again.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  SESSION_EXPIRED: 'Your session has expired. Please sign in again.',
}

function friendlyMessage(status: number, code?: string, rawMessage?: string): string {
  if (code && USER_FRIENDLY_MESSAGES[code]) {
    return USER_FRIENDLY_MESSAGES[code]
  }
  if (status === 401) return 'Your session has expired. Please sign in again.'
  if (status === 403) return 'You do not have permission to do that.'
  if (status === 404) return 'The requested resource was not found.'
  if (status === 422) return rawMessage ?? 'Please check your input and try again.'
  if (status === 429) return 'Too many requests. Please wait a moment and try again.'
  if (status >= 500) return 'Something went wrong on our end. Please try again in a moment.'
  return rawMessage ?? 'Something went wrong. Please try again.'
}

class ApiClientError extends Error {
  readonly status: number
  readonly code: string | undefined

  constructor({ status, message, code }: ApiErrorBody) {
    super(friendlyMessage(status, code, message))
    this.name = 'ApiClientError'
    this.status = status
    this.code = code
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      credentials: 'include',
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        Accept: 'application/json',
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })
  } catch {
    throw new ApiClientError({
      status: 0,
      message: 'Unable to connect. Please check your internet connection and try again.',
    })
  }

  if (response.status === 401 && !path.startsWith('/admin/auth/')) {
    window.location.href = '/login'
    throw new ApiClientError({ status: 401, message: 'Session expired' })
  }

  if (!response.ok) {
    let rawMessage = response.statusText
    let code: string | undefined
    try {
      const body = (await response.json()) as Record<string, unknown>
      const err = (body.error ?? body) as Record<string, unknown>
      rawMessage = (err.message as string) ?? rawMessage
      code = err.code as string | undefined
    } catch {
      // response wasn't JSON
    }
    throw new ApiClientError({ status: response.status, message: rawMessage, code })
  }

  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export const apiClient = {
  get: <T>(path: string): Promise<T> => request<T>('GET', path),
  post: <T>(path: string, body?: unknown): Promise<T> => request<T>('POST', path, body),
  put: <T>(path: string, body: unknown): Promise<T> => request<T>('PUT', path, body),
  patch: <T>(path: string, body: unknown): Promise<T> => request<T>('PATCH', path, body),
  del: <T = void>(path: string): Promise<T> => request<T>('DELETE', path),
}

export { ApiClientError }
